import React, { useEffect, useState } from 'react';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { t } from 'i18next';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { cn } from '@/lib/utils';
import LiveClassCard from './LiveClassCard';

export default function LiveClassesCalendarPlayful({ weekDays, setWeekDays, setNotAnsweredSurveys }) {
  const client = useClient();
  moment.locale(t('language.language'));
  const [firstWeekDay, setFirstWeekDay] = useState(moment().startOf('week').subtract(1, 'days'));
  const [lastWeekDay, setLastWeekDay] = useState(moment().endOf('week').subtract(1, 'days'));
  const [loading, setLoading] = useState(true);
  const currentWeekDay = moment().format('dddd');

  const week = [
    {
      weekDay: t('weekDays.sunday'),
      weekDayNumber: firstWeekDay.format('DD'),
      fullDate: moment(firstWeekDay).format('YYYY-MM-DD')
    },
    {
      weekDay: t('weekDays.monday'),
      weekDayNumber: moment(firstWeekDay).add(1, 'days').format('DD'),
      fullDate: moment(firstWeekDay).add(1, 'days').format('YYYY-MM-DD')
    },
    {
      weekDay: t('weekDays.tuesday'),
      weekDayNumber: moment(firstWeekDay).add(2, 'days').format('DD'),
      fullDate: moment(firstWeekDay).add(2, 'days').format('YYYY-MM-DD')
    },
    {
      weekDay: t('weekDays.wednesday'),
      weekDayNumber: moment(firstWeekDay).add(3, 'days').format('DD'),
      fullDate: moment(firstWeekDay).add(3, 'days').format('YYYY-MM-DD')
    },
    {
      weekDay: t('weekDays.thursday'),
      weekDayNumber: moment(firstWeekDay).add(4, 'days').format('DD'),
      fullDate: moment(firstWeekDay).add(4, 'days').format('YYYY-MM-DD')
    },
    {
      weekDay: t('weekDays.friday'),
      weekDayNumber: moment(firstWeekDay).add(5, 'days').format('DD'),
      fullDate: moment(firstWeekDay).add(5, 'days').format('YYYY-MM-DD')
    },
    {
      weekDay: t('weekDays.saturday'),
      weekDayNumber: lastWeekDay.format('DD'),
      fullDate: moment(lastWeekDay).format('YYYY-MM-DD')
    }
  ];

  const fetchLiveClasses = async () => {
    setLoading(true);
    try {
      const typeDateFormat = 'YYYY-MM-DD';
      const initialDate = moment().startOf('week').subtract(1, 'days').format(typeDateFormat);
      const finalDate = moment().endOf('week').subtract(1, 'days').format(typeDateFormat);
      const response = await client.fetch(`/live_classrooms?filter[date_between][initial]=${initialDate}&filter[date_between][final]=${finalDate}`);

      setNotAnsweredSurveys(response.data.filter(item => !item?.['questions-answered'] && moment(item?.['end-date']).isBefore(moment())));

      setWeekDays(response.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleNextWeek = async () => {
    setLoading(true);
    try {
      const initialDate = moment(firstWeekDay).add(7, 'days');
      const finalDate = moment(lastWeekDay).add(7, 'days');
      setFirstWeekDay(initialDate);
      setLastWeekDay(finalDate);

      const url = `/live_classrooms?filter[date_between][initial]=${initialDate.format('YYYY-MM-DD')}&filter[date_between][final]=${finalDate.format('YYYY-MM-DD')}`;
      const response = await client.fetch(url);
      setWeekDays(response.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handlePrevWeek = async () => {
    setLoading(true);
    try {
      const initialDate = moment(firstWeekDay).subtract(7, 'days');
      const finalDate = moment(lastWeekDay).subtract(7, 'days');
      setFirstWeekDay(initialDate);
      setLastWeekDay(finalDate);

      const url = `/live_classrooms?filter[date_between][initial]=${initialDate.format('YYYY-MM-DD')}&filter[date_between][final]=${finalDate.format('YYYY-MM-DD')}`;
      const response = await client.fetch(url);
      setWeekDays(response.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentWeekDay === 'Domingo') {
      handleNextWeek();
    } else {
      fetchLiveClasses();
    }
  }, []);

  return (
    <div
      className="rounded-[24px] border border-neutral-lightest p-5"
      style={{ background: 'linear-gradient(180deg, #F4FDFF 0%, #F2FCFF 44%, #FFFCF1 87.5%)' }}
    >
      <div className="relative mb-5 flex items-center justify-between border-b border-white/60 pb-4">
        <button
          type="button"
          aria-label="Semana anterior"
          onClick={handlePrevWeek}
          className="flex items-center gap-2 text-xs font-semibold text-neutral-dark"
        >
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
            style={{ background: 'var(--gradient-btn-continue)' }}
          >
            <FiArrowLeft className="h-4 w-4" />
          </span>
          {t('liveClass.lastWeek')}
        </button>

        <h2 className="absolute left-1/2 mb-0 -translate-x-1/2 text-center text-sm font-extrabold text-neutral-900">
          {t('liveClass.weekOf')} {firstWeekDay.format('DD/MM')} - {lastWeekDay.format('DD/MM')}
        </h2>

        <button
          type="button"
          aria-label="Próxima semana"
          onClick={handleNextWeek}
          className="flex items-center gap-2 text-xs font-semibold text-neutral-dark"
        >
          {t('liveClass.nextWeek')}
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white"
            style={{ background: 'var(--gradient-btn-continue)' }}
          >
            <FiArrowRight className="h-4 w-4" />
          </span>
        </button>
      </div>

      {loading && <Loader />}

      {!loading && weekDays.length === 0 && (
        <EmptyState
          type="data"
          title="Sem aulas ao vivo nesta semana"
          variant="plain"
          className="pt-32"
        />
      )}

      {weekDays.length > 0 && !loading && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-7">
          {week.map(day => {
            const isCurrent = day.weekDay === currentWeekDay;
            const dayLiveClasses = weekDays.filter(liveClass => moment(liveClass['initial-date']).format('YYYY-MM-DD') === day.fullDate);

            return (
              <div
                key={day.weekDay}
                className={cn('rounded-2xl p-3', isCurrent && 'bg-white/60')}
              >
                <div className={cn('mb-4 flex items-baseline gap-1.5 text-sm font-bold', isCurrent ? 'text-primary-dark' : 'text-neutral-dark')}>
                  <span>{day.weekDayNumber}</span>
                  <span>{day.weekDay}</span>
                </div>

                <div className="flex flex-col gap-3">
                  {dayLiveClasses.map(liveClass => (
                    <LiveClassCard
                      key={liveClass.id}
                      liveClass={liveClass}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
